import { useEffect, useMemo, useState } from 'react'
import { Select } from 'antd'
import { toast } from 'react-hot-toast'
import { useLocation, useNavigate } from 'react-router-dom'
import { ButtonFilledAutoWidth } from '../../components/button'
import { TextInput } from '../../components/input'
import GoBack from '../../components/back/GoBack'
import { GetFileStandardService } from '../../services/admin/FileStandardServices'
import { GetClassStreamService } from '../../services/admin/StudentStreamServices'
import { CreateSubjectService, UpdateSubjectService } from '../../services/admin/SubjectServices'
import SettingsMenu from '../staticscreens/SettingsMenu'

function SubjectCE() {
    const [data, setdata] = useState({
        name: '',
        total_chapters: 0,
        completed_chapters: 0,
        sort_order: 0,
        filter_standard: undefined,
        stream: undefined,
        substream: undefined
    })
    const [error, seterror] = useState({ name: '' })
    const [standards, setstandards] = useState([])
    const [streams, setstreams] = useState([])
    const [substreams, setsubstreams] = useState([])

    const { state } = useLocation()
    const navigate = useNavigate()

    useEffect(() => {
        getOptions()
    }, [])

    useEffect(() => {
        if (!state?._id) {
            return
        }

        setdata((prev) => ({
            ...prev,
            ...state,
            filter_standard: state?.filter_standard?._id || state?.filter_standard || undefined,
            stream: state?.stream || undefined,
            substream: state?.substream || undefined
        }))
    }, [state])

    async function getOptions() {
        const [standardResponse, streamResponse] = await Promise.all([
            GetFileStandardService(),
            GetClassStreamService()
        ])

        const streamDatas = streamResponse?.data?.datas || []

        setstandards((standardResponse?.data?.datas || []).map((item) => ({
            value: item._id,
            label: item.standard || item.label,
            data: item
        })))

        setstreams(streamDatas.filter((item) => !item?.parent_stream).map((item) => ({
            value: item.name,
            label: item.name,
            data: item
        })))

        setsubstreams(streamDatas.filter((item) => item?.parent_stream).map((item) => ({
            value: item.name,
            label: item.name,
            data: item
        })))
    }

    function handlechange(e) {
        setdata((prev) => ({ ...prev, [e.target.name]: e.target.value }))
        seterror((prev) => ({ ...prev, [e.target.name]: '' }))
    }

    async function submitform() {
        if (!data.name?.trim()) {
            seterror((prev) => ({ ...prev, name: 'Name field is required *' }))
            return
        }

        const send_data = {
            name: data.name,
            total_chapters: Number(data.total_chapters || 0),
            completed_chapters: Number(data.completed_chapters || 0),
            sort_order: Number(data.sort_order || 1)
        }

        if (data.filter_standard) {
            send_data.filter_standard = data.filter_standard
        }
        if (data.stream) {
            send_data.stream = data.stream
        }
        if (data.substream) {
            send_data.substream = data.substream
        }

        if (!state?._id) {
            const response = await CreateSubjectService(send_data)
            if (response?.status === 201) {
                resetform()
                toast.success('Subject Created Successfully')
            }
            return
        }

        const response = await UpdateSubjectService(send_data, state?._id)
        if (response?.status === 200) {
            resetform()
            navigate(-1)
            toast.success('Subject Updated Successfully')
        }
    }

    function resetform() {
        setdata({
            name: '',
            total_chapters: 12,
            completed_chapters: 0,
            sort_order: 1,
            filter_standard: undefined,
            stream: undefined,
            substream: undefined
        })
        seterror({ name: '' })
    }

    const visibleSubstreams = useMemo(() => {
        return substreams.filter((item) => {
            if (!data.stream) {
                return true
            }

            const parentStream = item?.data?.parent_stream?.name || item?.data?.parent_stream
            return parentStream === data.stream
        })
    }, [substreams, data.stream])

    return (
        <div className='md:flex'>
            <div className='hidden md:block'>
                <SettingsMenu />
            </div>
            <div className='sm:w-full lg:w-80 px-4 pt-5'>
                <GoBack />

                <h6 className='font-[700]'>{state?._id ? 'Edit' : 'Add'} Subject</h6>
                <h6 className='text-[10px] bg-slate-100 p-2 font-[500] leading-snug'>Use the below form to create or edit the <b>Subject</b> with filter standard and stream-based options.</h6>

                <TextInput
                    label={'Name'}
                    variant='standard'
                    name='name'
                    type='text'
                    error={error.name}
                    value={data.name}
                    handlechange={handlechange}
                    placeholder='Enter subject name'
                    InputLabelProps={{ style: { color: '#fff' } }}
                />

                <div>
                    <h6 className='text-[11px] mb-1 mt-2'>Filter Standard</h6>
                    <Select
                        value={data.filter_standard}
                        allowClear
                        bordered={false}
                        placeholder=''
                        onChange={(value) => setdata((prev) => ({ ...prev, filter_standard: value || undefined }))}
                        options={standards}
                        className='w-full border outline-0 focus:outline-0 focus:ring-0 focus:border-transparent'
                    />
                </div>

                <div>
                    <h6 className='text-[11px] mb-1 mt-2'>Stream</h6>
                    <Select
                        value={data.stream}
                        allowClear
                        bordered={false}
                        placeholder=''
                        onChange={(value) => setdata((prev) => ({ ...prev, stream: value || undefined, substream: undefined }))}
                        options={streams}
                        className='w-full border outline-0 focus:outline-0 focus:ring-0 focus:border-transparent'
                    />
                </div>

                <div>
                    <h6 className='text-[11px] mb-1 mt-2'>Sub Stream</h6>
                    <Select
                        value={data.substream}
                        allowClear
                        bordered={false}
                        placeholder=''
                        onChange={(value) => setdata((prev) => ({ ...prev, substream: value || undefined }))}
                        options={visibleSubstreams}
                        className='w-full border outline-0 focus:outline-0 focus:ring-0 focus:border-transparent'
                    />
                </div>

                <TextInput
                    label={'Total Chapters'}
                    variant='standard'
                    name='total_chapters'
                    type='number'
                    value={data.total_chapters}
                    handlechange={handlechange}
                    placeholder='12'
                    InputLabelProps={{ style: { color: '#fff' } }}
                />

                <TextInput
                    label={'Completed Chapters'}
                    variant='standard'
                    name='completed_chapters'
                    type='number'
                    value={data.completed_chapters}
                    handlechange={handlechange}
                    placeholder='0'
                    InputLabelProps={{ style: { color: '#fff' } }}
                />

                <TextInput
                    label={'Sort Order'}
                    variant='standard'
                    name='sort_order'
                    type='number'
                    value={data.sort_order}
                    handlechange={handlechange}
                    placeholder='1'
                    InputLabelProps={{ style: { color: '#fff' } }}
                />

                <div className='mt-5'>
                    <ButtonFilledAutoWidth btnName={state?._id ? 'UPDATE SUBJECT' : 'ADD SUBJECT'} onClick={submitform} />
                </div>
            </div>
        </div>
    )
}

export default SubjectCE
